import React from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';

import AboutUsCoreModel from '../models/AboutUsCore/AboutUsCore';

import Layout from '../components/layout';
import renderSEO from '../components/about-us/render-seo';
import renderHeroSection from '../components/about-us/hero-section';
import PointOfViewSection from '../components/point-of-view-section';
import RelatedInsights from '../components/related-insights';
import FooterCTA from '../components/contact-us/footer-cta';
import { VideoCarousel } from '../components/video-carousel';
import ImageWithTitleset from '../components/about-us/image-with-titleset';
import ClientQuote from "../components/client-quote";
import FeaturedContentCard from "../components/featured-content-card";

import { colors, Paper } from '../ui-components';

/**
 * Component that renders an About Us Core's content
 *
 * @component AboutUsCore
 * 
 * @param {object} props
 * 
 * @prop {object} data
 * @prop {object} pageContext
 * 
 * @returns {JSX} returns about us core template
 */
const AboutUsCore = props => {
  const { data, pageContext } = props;
  const aboutUsCore = new AboutUsCoreModel(data.contentfulAboutUsCore);
  const level = 'core';
  const {
    pageTitle,
    metaDescription,
    slug,
    heroSectionBody,
    heroSectionLabel,
    heroSectionImages,
    spotlight,
    primaryHighlight,
    videoCarousel,
    featuredContent,
    featuredContentDownload,
    exploreMore,
    editorial,
    relatedInsights,
    footerCta
  } = aboutUsCore;

  const renderSpotlight = () => {
    if (spotlight?.type === 'ContentfulPointOfViewBlock') {
      return <ImageWithTitleset
        contentBlock={spotlight}
        imageLeft={spotlight?.imageLeft}
        geoSpace={pageContext.space}
        centerTextSection
      />
    } else if (spotlight?.type === 'ContentfulQuoteBlock') {
      return <ClientQuote quote={spotlight} layout={'side-by-side'} />
    }
  };

  const renderPrimaryHighlight = () => {
    return <PointOfViewSection pointOfView={primaryHighlight} geoSpace={pageContext.space} featuredContent />
  };

  const renderVideoCarousel = () => {
    return (
      <CarouselWrapper>
        <VideoCarousel carouselVideos={videoCarousel.videos} subtitle={videoCarousel.title} hideLabel />
      </CarouselWrapper>
    )
  };

  const renderExploreMore = () => {
    const contentPanels = exploreMore.map((povBlock, index) => {
      return (
        <ImageWithTitleset
          imageLeft={index % 2 != 0}
          contentBlock={povBlock}
          altMobileOrder={index % 2 == 0}
          centerTextSection
        />
      )
    });

    return <ContentPanelSection>{contentPanels}</ContentPanelSection>;
  };

  const renderEditorial = () => {
    return (
      <>
        <Paper bottom subtle />
        <EditorialWrapper>
          <ImageWithTitleset
            imageLeft={editorial?.imageLeft}
            contentBlock={editorial}
            geoSpace={pageContext.space}
            editorial
          />
        </EditorialWrapper>
        <Paper top subtle />
      </>
    )
  };

  const renderRelatedInsights = () => {
    return <RelatedInsights insights={relatedInsights} />
  };

  const renderFooterCta = () => {
    return <FooterCTA footerCta={footerCta} pageGeo={pageContext.space} />
  };

  return (
    <Layout>
      {renderSEO(pageTitle, metaDescription, slug, heroSectionImages, pageContext)}
      <Paper bottom>
        {renderHeroSection(pageTitle, heroSectionBody, heroSectionLabel, heroSectionImages, level)}
        {spotlight && renderSpotlight()}
      </Paper>
      {primaryHighlight && renderPrimaryHighlight()}
      {videoCarousel
        ? renderVideoCarousel()
        : featuredContent && (
            // TODO 19966: To allow featuredContent to be a Case Study, update the featuredContent field query in the about us detail fragment to include an on ContentfulCaseStudy case
            <FeaturedContentCard downloadContent={featuredContentDownload} featuredContentRaw={featuredContent} />
          )}
      <Paper top subtle overlay={featuredContent} overlayMargin={250}>
        {exploreMore && renderExploreMore()}
        {editorial && renderEditorial()}
        {relatedInsights && renderRelatedInsights()}
      </Paper>
      {footerCta && renderFooterCta()}
    </Layout>
  );
};

export default AboutUsCore;

export const pageQuery = graphql`
  query AboutUsCoreBySlug($slug: String!, $spaceId: String) {
    contentfulAboutUsCore(slug: { eq: $slug }, spaceId: { eq: $spaceId }) {
      ...CompleteAboutUsCore
    }
  }
`;

const ContentPanelSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const EditorialWrapper = styled.div`
  background-color: ${colors.pureWhite};
  
  #image-with-title-set-container {
    overflow: hidden;
    padding-bottom: 0;
  }
`;

const CarouselWrapper = styled.div`
  #video-carousel {
    padding-bottom: 2px; //2px to account for the border
  }
`;
